import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Play, Ticket, Star, Clock, Percent, Armchair, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { EventCard } from '../components/events/EventCard';
import { SEOHead } from '../components/SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_HERO_SLIDES = [
  {
    image_url: 'https://images.pexels.com/photos/10588382/pexels-photo-10588382.jpeg',
    title: 'El Arte',
    subtitle: 'en Pantalla',
    description: 'Descubre las mejores producciones de ópera, ballet, conciertos y teatro.'
  }
];

const CITY_IMAGES = {
  'Madrid': 'https://images.pexels.com/photos/919282/pexels-photo-919282.jpeg',
  'Barcelona': 'https://images.pexels.com/photos/14786691/pexels-photo-14786691.jpeg',
  'Valencia': 'https://images.pexels.com/photos/32317216/pexels-photo-32317216.jpeg'
};

// Partners Section with navigation arrows
const PartnersSection = ({ partners }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerPage = 4;
  const totalPages = Math.ceil(partners.length / itemsPerPage);
  
  const nextPage = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  };
  
  const prevPage = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };
  
  const visiblePartners = partners.slice(
    currentIndex * itemsPerPage,
    currentIndex * itemsPerPage + itemsPerPage
  );
  
  return (
    <section className="py-16 md:py-20 bg-[var(--surface)]" data-testid="partners-section">
      <div className="section-container">
        <div className="text-center mb-12">
          <span className="text-[var(--primary)] text-xs uppercase tracking-[0.3em] block mb-4">
            Colaboradores
          </span>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white">
            Nuestros Partners
          </h2>
        </div>
        
        <div className="relative">
          {/* Navigation Arrows */}
          {partners.length > itemsPerPage && (
            <>
              <button
                onClick={prevPage}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 bg-[var(--primary)] text-black flex items-center justify-center hover:bg-[var(--primary)]/80 transition-colors"
                aria-label="Anterior"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={nextPage}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 bg-[var(--primary)] text-black flex items-center justify-center hover:bg-[var(--primary)]/80 transition-colors"
                aria-label="Siguiente"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}
          
          {/* Partners Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 px-8">
            {visiblePartners.map((partner, index) => (
              <a
                key={index}
                href={partner.website_url || '#'}
                target={partner.website_url ? '_blank' : '_self'}
                rel="noopener noreferrer"
                className="group flex flex-col items-center"
                data-testid={`partner-logo-${currentIndex * itemsPerPage + index}`}
              >
                <div className="w-full h-24 md:h-28 flex items-center justify-center bg-white/5 border border-white/10 hover:border-[var(--primary)]/30 transition-all duration-300 p-4">
                  <img 
                    src={partner.logo_url} 
                    alt={partner.name || 'Partner'}
                    className="max-w-full max-h-full object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                  />
                </div>
                {partner.name && (
                  <span className="mt-2 text-white/50 text-sm group-hover:text-[var(--primary)] transition-colors">
                    {partner.name}
                  </span>
                )}
              </a>
            ))}
          </div>
          
          {/* Page Indicators */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx === currentIndex ? 'bg-[var(--primary)]' : 'bg-white/20 hover:bg-white/40'
                  }`}
                  aria-label={`Página ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export const Home = () => {
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [heroSlides, setHeroSlides] = useState(DEFAULT_HERO_SLIDES);
  const [partners, setPartners] = useState([]);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (heroSlides.length > 1) {
      const interval = setInterval(() => {
        setCurrentHeroIndex(prev => (prev + 1) % heroSlides.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [heroSlides]);

  const fetchData = async () => {
    try {
      const [featuredRes, upcomingRes, configRes] = await Promise.all([
        axios.get(`${API}/events/featured`),
        axios.get(`${API}/events/upcoming?limit=4`),
        axios.get(`${API}/site-config`)
      ]);
      setFeaturedEvents(featuredRes.data);
      setUpcomingEvents(upcomingRes.data);
      if (configRes.data.hero_slides?.length > 0) {
        setHeroSlides(configRes.data.hero_slides);
      }
      if (configRes.data.partners?.length > 0) {
        setPartners(configRes.data.partners);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const genreLabels = {
    opera: 'Ópera',
    ballet: 'Ballet',
    concierto: 'Conciertos',
    teatro: 'Teatro',
    musical: 'Musicales',
    cine: 'Cine'
  };

  const currentSlide = heroSlides[currentHeroIndex] || DEFAULT_HERO_SLIDES[0];

  return (
    <div data-testid="home-page">
      <SEOHead 
        title="Inicio"
        description="Event Cinema - Disfruta de ópera, ballet, conciertos y teatro en directo desde la Royal Opera House, Arena de Verona y más. Retransmisiones en cines de toda España con hasta 25% de descuento."
        keywords="ópera en cines España, ballet en directo, Royal Opera House Madrid, Arena de Verona Barcelona, conciertos en pantalla grande, teatro en cine, La Traviata, El Lago de los Cisnes, eventos culturales"
        canonicalUrl="/"
      />
      
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className="hero-background transition-opacity duration-1000"
            style={{
              backgroundImage: `url(${slide.image_url})`,
              opacity: index === currentHeroIndex ? 1 : 0
            }}
          />
        ))}
        <div className="hero-overlay" />
        
        <div className="relative z-10 section-container w-full">
          <div className="max-w-4xl animate-slideUp">
            <span className="text-[var(--primary)] text-xs uppercase tracking-[0.3em] mb-4 block">
              Experiencias Culturales Únicas
            </span>
            <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-none mb-6">
              {currentSlide.title}<br />
              <span className="text-[var(--primary)]">{currentSlide.subtitle}</span>
            </h1>
            <p className="text-white/60 text-lg md:text-xl max-w-2xl mb-10 leading-relaxed">
              {currentSlide.description}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/catalogo" className="btn-primary inline-flex items-center gap-3">
                Ver Catálogo
                <ArrowRight size={16} />
              </Link>
              <Link to="/calendario" className="btn-outline inline-flex items-center gap-3">
                <Calendar size={16} />
                Calendario
              </Link>
            </div>
          </div>
        </div>

        {/* Hero indicators */}
        {heroSlides.length > 1 && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentHeroIndex(index)}
                className={`w-12 h-1 transition-all duration-300 ${
                  index === currentHeroIndex ? 'bg-[var(--primary)]' : 'bg-white/30'
                }`}
                data-testid={`hero-indicator-${index}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* SMART Sale Promo Banner */}
      <section className="relative overflow-hidden bg-gradient-to-r from-[#0a0a0a] via-[#1a1205] to-[#0a0a0a]" data-testid="smart-promo-section">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-[var(--primary)]/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-[var(--primary)]/5 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-px bg-gradient-to-r from-transparent via-[var(--primary)]/20 to-transparent" />
        </div>
        
        <div className="section-container py-16 md:py-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--primary)]/10 border border-[var(--primary)]/30 mb-6">
                <Ticket size={16} className="text-[var(--primary)]" />
                <span className="text-[var(--primary)] text-xs uppercase tracking-[0.2em] font-semibold">Venta Exclusiva</span>
              </div>
              
              <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-white mb-4 leading-tight">
                Venta <span className="text-[var(--primary)]">SMART</span>
              </h2>
              
              <p className="text-white/60 text-lg md:text-xl mb-8 max-w-xl mx-auto lg:mx-0">
                Regístrate gratis y accede a <span className="text-[var(--primary)] font-semibold">descuentos exclusivos de hasta el 25%</span> en tus entradas para los mejores eventos culturales.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link 
                  to="/mi-cuenta" 
                  className="btn-primary inline-flex items-center justify-center gap-3 text-base px-8 py-4"
                  data-testid="smart-register-btn"
                >
                  <Star size={18} />
                  Regístrate Gratis
                </Link>
                <Link 
                  to="/catalogo?smart=true" 
                  className="btn-outline inline-flex items-center justify-center gap-3 text-base px-8 py-4"
                  data-testid="smart-catalog-btn"
                >
                  Ver Eventos con Descuento
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
            
            {/* Right: Benefits Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Benefit 1 */}
              <div className="group p-6 bg-white/5 border border-white/10 hover:border-[var(--primary)]/50 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-[var(--primary)]/10 mb-4">
                  <Percent size={24} className="text-[var(--primary)]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">Hasta 25% Descuento</h3>
                <p className="text-white/50 text-sm">Precios exclusivos para miembros registrados en eventos seleccionados.</p>
              </div>
              
              {/* Benefit 2 */}
              <div className="group p-6 bg-white/5 border border-white/10 hover:border-[var(--primary)]/50 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-[var(--primary)]/10 mb-4">
                  <Clock size={24} className="text-[var(--primary)]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">Venta Anticipada</h3>
                <p className="text-white/50 text-sm">La venta se cierra 48 horas antes del evento.</p>
              </div>
              
              {/* Benefit 3 */}
              <div className="group p-6 bg-white/5 border border-white/10 hover:border-[var(--primary)]/50 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-[var(--primary)]/10 mb-4">
                  <Star size={24} className="text-[var(--primary)]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">100% Gratuito</h3>
                <p className="text-white/50 text-sm">Sin cuotas ni compromisos. Regístrate y empieza a ahorrar.</p>
              </div>
              
              {/* Benefit 4 */}
              <div className="group p-6 bg-white/5 border border-white/10 hover:border-[var(--primary)]/50 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center bg-[var(--primary)]/10 mb-4">
                  <Ticket size={24} className="text-[var(--primary)]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">Compra Fácil</h3>
                <p className="text-white/50 text-sm">Proceso de compra rápido y seguro directamente en nuestra web.</p>
              </div>
            </div>
          </div>
          
          {/* Bottom Stats */}
          <div className="mt-12 pt-8 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="font-['Playfair_Display'] text-3xl md:text-4xl text-[var(--primary)] mb-1">25%</p>
              <p className="text-white/50 text-sm uppercase tracking-wider">Descuento Máximo</p>
            </div>
            <div className="text-center">
              <p className="font-['Playfair_Display'] text-3xl md:text-4xl text-[var(--primary)] mb-1">0€</p>
              <p className="text-white/50 text-sm uppercase tracking-wider">Coste de Registro</p>
            </div>
            <div className="text-center">
              <p className="font-['Playfair_Display'] text-3xl md:text-4xl text-[var(--primary)] mb-1">48h</p>
              <p className="text-white/50 text-sm uppercase tracking-wider">Cierre Venta SMART</p>
            </div>
            <div className="text-center">
              <Armchair size={40} className="text-[var(--primary)] mx-auto mb-1" />
              <p className="text-white/50 text-sm uppercase tracking-wider">Butacas Seleccionadas</p>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events - First */}
      {upcomingEvents.length > 0 && (
        <section className="section-container section-spacing" data-testid="upcoming-section">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-[var(--primary)] text-xs uppercase tracking-[0.3em] block mb-4">
                Próximamente
              </span>
              <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white">
                Próximos Eventos
              </h2>
            </div>
            <Link 
              to="/calendario" 
              className="btn-ghost inline-flex items-center gap-2"
              data-testid="view-calendar"
            >
              Ver calendario
              <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {upcomingEvents.slice(0, 4).map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </section>
      )}

      {/* Featured Events - Second */}
      {featuredEvents.length > 0 && (
        <section className="section-container section-spacing bg-[var(--surface)]" data-testid="featured-section">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-[var(--primary)] text-xs uppercase tracking-[0.3em] block mb-4">
                Destacados
              </span>
              <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white">
                No te lo pierdas
              </h2>
            </div>
            <Link 
              to="/catalogo" 
              className="btn-ghost inline-flex items-center gap-2"
              data-testid="view-all-featured"
            >
              Ver todos
              <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredEvents.slice(0, 4).map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </section>
      )}

      {/* Genres Section - Third */}
      <section className="section-container section-spacing bg-[var(--surface)]" data-testid="genres-section">
        <div className="text-center mb-16">
          <span className="text-[var(--primary)] text-xs uppercase tracking-[0.3em] block mb-4">
            Explora
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white">
            Por Género
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(genreLabels).map(([value, label], index) => (
            <Link
              key={value}
              to={`/catalogo?genre=${value}`}
              className="group p-8 border border-white/10 hover:border-[var(--primary)] transition-all duration-500 text-center"
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`genre-link-${value}`}
            >
              <span className="text-4xl mb-4 block">
                {value === 'opera' && '🎭'}
                {value === 'ballet' && '🩰'}
                {value === 'concierto' && '🎼'}
                {value === 'teatro' && '🎪'}
                {value === 'musical' && '🎵'}
                {value === 'cine' && '🎬'}
              </span>
              <span className="text-white/70 group-hover:text-[var(--primary)] transition-colors text-sm uppercase tracking-widest">
                {label}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Cities Section */}
      <section className="section-container section-spacing" data-testid="cities-section">
        <div className="text-center mb-16">
          <span className="text-[var(--primary)] text-xs uppercase tracking-[0.3em] block mb-4">
            Encuentra eventos
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white">
            Por Ciudad
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries(CITY_IMAGES).map(([city, image]) => (
            <Link
              key={city}
              to={`/ciudades?city=${city}`}
              className="city-card group"
              data-testid={`city-card-${city.toLowerCase()}`}
            >
              <div 
                className="city-card-image"
                style={{ backgroundImage: `url(${image})` }}
              />
              <div className="city-card-overlay" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h3 className="font-['Playfair_Display'] text-3xl text-white group-hover:text-[var(--primary)] transition-colors">
                  {city}
                </h3>
                <span className="text-white/50 text-sm flex items-center gap-2 mt-2">
                  <MapPin size={14} />
                  Ver eventos
                </span>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/ciudades" className="btn-outline" data-testid="view-all-cities">
            Ver todas las ciudades
          </Link>
        </div>
      </section>

      {/* Partners Section */}
      {partners.length > 0 && (
        <PartnersSection partners={partners} />
      )}

      {/* CTA Section */}
      <section className="section-container section-spacing">
        <div 
          className="relative overflow-hidden p-12 md:p-20"
          style={{
            backgroundImage: `url(https://images.pexels.com/photos/4722577/pexels-photo-4722577.jpeg)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative z-10 text-center max-w-3xl mx-auto">
            <h2 className="font-['Playfair_Display'] text-4xl md:text-6xl text-white mb-6">
              La Cultura al Alcance de Todos
            </h2>
            <p className="text-white/60 text-lg mb-10">
              Vive las mejores producciones desde la butaca de tu cine favorito.
              Ópera, ballet, conciertos y teatro en la mejor calidad.
            </p>
            <Link to="/catalogo" className="btn-primary inline-flex items-center gap-3">
              <Play size={16} />
              Explorar Eventos
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
